import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Send, Mail, Bell, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAnnouncements() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    subject: '',
    headline: '',
    body: '',
    cta_label: 'Open the App',
    cta_link: '',
  });
  const [channels, setChannels] = useState({ push: true, email: true, inapp: true });
  const [audience, setAudience] = useState({ members: true, fundraisers: true, admins: true });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  if (user && !user.is_admin) {
    navigate('/');
    return null;
  }

  const send = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || form.subject.length < 3) return toast.error('Subject is required (min 3 chars)');
    if (!form.headline.trim()) return toast.error('Headline is required');
    if (!form.body.trim()) return toast.error('Body is required');
    const selected = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) return toast.error('Pick at least one channel');
    const selectedAudience = Object.entries(audience).filter(([, v]) => v).map(([k]) => k);
    if (selectedAudience.length === 0) return toast.error('Pick at least one audience');

    if (!window.confirm(`This will broadcast to ${selectedAudience.join(' + ')} via ${selected.join(', ')}. Continue?`)) return;

    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/admin/announce`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: form.subject.trim(),
          headline: form.headline.trim(),
          body: form.body.trim(),
          cta_label: form.cta_label.trim() || 'Open the App',
          cta_link: form.cta_link.trim() || null,
          channels: selected,
          audience: selectedAudience,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        setResult(d);
        toast.success(`Sent to ${d.recipients} members`);
        setForm({ subject: '', headline: '', body: '', cta_label: 'Open the App', cta_link: '' });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send');
      }
    } finally {
      setSending(false);
    }
  };

  const Channel = ({ k, label, icon: Icon }) => (
    <button
      type="button"
      onClick={() => setChannels({ ...channels, [k]: !channels[k] })}
      data-testid={`channel-${k}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
        channels[k]
          ? 'bg-bundle-green text-white border-bundle-green'
          : 'bg-white text-gray-600 border-gray-300 hover:border-bundle-green/40'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  const Audience = ({ k, label }) => (
    <button
      type="button"
      onClick={() => setAudience({ ...audience, [k]: !audience[k] })}
      data-testid={`audience-${k}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
        audience[k]
          ? 'bg-bundle-gold text-bundle-green-dark border-bundle-gold'
          : 'bg-white text-gray-600 border-gray-300 hover:border-bundle-gold/60'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-bundle-cream">
      <div className="max-w-2xl mx-auto p-4 pb-mobile-nav">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="announce-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-heading font-bold text-bundle-green flex items-center gap-2">
            <Send className="w-6 h-6" /> Send Announcement
          </h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Broadcast to your community</CardTitle>
            <p className="text-xs text-gray-500">Choose your audience (Members, Fundraisers, Admins) and the channels to reach them on.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={send} className="space-y-4">
              <div>
                <Label htmlFor="subject">Subject (push title + email subject)</Label>
                <Input
                  id="subject"
                  data-testid="announce-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="✨ Bundle News, Welcomes, and CTP tracking are live"
                  maxLength={120}
                  required
                />
              </div>
              <div>
                <Label htmlFor="headline">Headline (shown at top of email)</Label>
                <Input
                  id="headline"
                  data-testid="announce-headline"
                  value={form.headline}
                  onChange={(e) => setForm({ ...form, headline: e.target.value })}
                  placeholder="What's new on The Bundle Golf Club"
                  maxLength={160}
                  required
                />
              </div>
              <div>
                <Label htmlFor="body">Body (use line breaks freely — they'll show up in the email)</Label>
                <Textarea
                  id="body"
                  data-testid="announce-body"
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder={"This week we shipped:\n\n• Bundle News on your dashboard\n• Welcome cards when new members join\n• CTP tracking on the scorecard scanner\n\nGet out there and play."}
                  rows={8}
                  required
                />
                <p className="text-[10px] text-gray-500 mt-1">Plain text — line breaks are preserved.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cta_label">CTA Button Label</Label>
                  <Input
                    id="cta_label"
                    data-testid="announce-cta-label"
                    value={form.cta_label}
                    onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                    placeholder="Open the App"
                    maxLength={40}
                  />
                </div>
                <div>
                  <Label htmlFor="cta_link">CTA Link (optional)</Label>
                  <Input
                    id="cta_link"
                    data-testid="announce-cta-link"
                    value={form.cta_link}
                    onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
                    placeholder="https://thebundlegolfclubusa.com or /events"
                  />
                </div>
              </div>

              <div>
                <Label className="block mb-2">Audience</Label>
                <div className="flex gap-2 flex-wrap">
                  <Audience k="members" label="Members" />
                  <Audience k="fundraisers" label="Fundraisers" />
                  <Audience k="admins" label="Directors" />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Recipients are de-duplicated across overlapping roles.</p>
              </div>

              <div>
                <Label className="block mb-2">Channels</Label>
                <div className="flex gap-2 flex-wrap">
                  <Channel k="push" label="Push (OneSignal)" icon={Bell} />
                  <Channel k="email" label="Email" icon={Mail} />
                  <Channel k="inapp" label="In-App Bell" icon={MessageSquare} />
                </div>
              </div>

              <Button
                type="submit"
                disabled={sending}
                data-testid="announce-send-btn"
                className="w-full bg-bundle-green hover:bg-bundle-green-dark text-white"
              >
                {sending ? 'Sending…' : 'Send Announcement'}
              </Button>
            </form>

            {result && (
              <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm" data-testid="announce-result">
                <p className="font-bold text-emerald-700">✓ Broadcast sent</p>
                <p className="text-emerald-700">{result.recipients} recipients · {result.push_sent} push · {result.email_sent} email · {result.inapp_sent} in-app</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
